import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { SHIFT_TYPES, SHIFT_STATUS } from '../data/nurseAppModels';
import { notificationModel } from '../data/notificationModel';

const ShiftContext = createContext();

export const useShift = () => {
  const context = useContext(ShiftContext);
  if (!context) {
    throw new Error('useShift must be used within ShiftProvider');
  }
  return context;
};

export const ShiftProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch shifts for current team
  useEffect(() => {
    if (!currentTeam) {
      setShifts([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shifts'),
        where('teamId', '==', currentTeam.id),
        orderBy('startTime', 'desc')
      ),
      (snapshot) => {
        const shiftsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setShifts(shiftsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching shifts:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Create Shift
  const createShift = async (shiftData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check permissions (only Leader and Deputy Leaders can create shifts)
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์สร้างเวร');
      }

      const newShift = {
        teamId: currentTeam.id,
        shiftType: shiftData.shiftType,
        startTime: Timestamp.fromDate(new Date(shiftData.startTime)),
        endTime: Timestamp.fromDate(new Date(shiftData.endTime)),
        assignedTo: shiftData.assignedTo || currentUser.uid,
        assignedToName: shiftData.assignedToName || currentUser.displayName || currentUser.email,
        department: shiftData.department || currentTeam.department || '',
        ward: shiftData.ward || currentTeam.ward || '',
        notes: shiftData.notes || '',
        status: SHIFT_STATUS.SCHEDULED,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const shiftRef = await addDoc(collection(db, 'shifts'), newShift);

      // Send notification to assigned user if assigned
      if (shiftData.assignedTo && shiftData.assignedTo !== currentUser.uid) {
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            shiftData.assignedTo,
            notificationModel.types.SHIFT_ASSIGNED,
            '📅 คุณได้รับมอบหมายเวรใหม่',
            `คุณได้รับมอบหมายเวร${shiftData.shiftType === SHIFT_TYPES.MORNING ? 'เช้า' : shiftData.shiftType === SHIFT_TYPES.AFTERNOON ? 'บ่าย' : 'ดึก'} ในวันที่ ${new Date(shiftData.startTime).toLocaleDateString('th-TH')}`,
            {
              shiftId: shiftRef.id,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
          // Don't throw error, just log it
        }
      }

      return { success: true, shiftId: shiftRef.id };
    } catch (err) {
      console.error('Error creating shift:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Clock In
  const clockIn = async (shiftId, location = null) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const shiftRef = doc(db, 'shifts', shiftId);
      const shiftSnap = await getDoc(shiftRef);

      if (!shiftSnap.exists()) {
        throw new Error('ไม่พบเวร');
      }

      const shiftData = shiftSnap.data();

      if (shiftData.assignedTo !== currentUser.uid) {
        throw new Error('คุณไม่ได้ถูกมอบหมายให้เวรนี้');
      }

      await updateDoc(shiftRef, {
        status: SHIFT_STATUS.IN_PROGRESS,
        clockInTime: serverTimestamp(),
        clockInLocation: location,
        updatedAt: serverTimestamp()
      });

      // Send notification to team leader (optional)
      try {
        const teamRef = doc(db, 'teams', shiftData.teamId);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const team = teamSnap.data();
          if (team.leaderId && team.leaderId !== currentUser.uid) {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              team.leaderId,
              notificationModel.types.SHIFT_ASSIGNED,
              '✅ สมาชิกเข้าเวรแล้ว',
              `${currentUser.displayName || currentUser.email} เข้าเวรแล้ว`,
              {
                shiftId: shiftId,
                teamId: shiftData.teamId
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      // Award rewards for clock in
      try {
        const { awardRewards, ACTIVITY_TYPES } = await import('../utils/gamificationManager');
        const { addTCoins } = await import('./TCoinsContext');
        // Note: We need to get addTCoins from context, but we'll use window event for now
        window.dispatchEvent(new CustomEvent('awardActivity', {
          detail: {
            activityType: ACTIVITY_TYPES.CLOCK_IN,
            metadata: {
              shiftId,
              teamId: shiftData.teamId
            }
          }
        }));
      } catch (rewardError) {
        console.error('Error awarding rewards:', rewardError);
        // Don't fail the clock in if rewards fail
      }

      return { success: true };
    } catch (err) {
      console.error('Error clocking in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Clock Out
  const clockOut = async (shiftId) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const shiftRef = doc(db, 'shifts', shiftId);
      const shiftSnap = await getDoc(shiftRef);

      if (!shiftSnap.exists()) {
        throw new Error('ไม่พบเวร');
      }

      const shiftData = shiftSnap.data();

      if (shiftData.assignedTo !== currentUser.uid) {
        throw new Error('คุณไม่ได้ถูกมอบหมายให้เวรนี้');
      }

      await updateDoc(shiftRef, {
        status: SHIFT_STATUS.COMPLETED,
        clockOutTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Award rewards for clock out
      try {
        const { ACTIVITY_TYPES } = await import('../utils/gamificationManager');
        window.dispatchEvent(new CustomEvent('awardActivity', {
          detail: {
            activityType: ACTIVITY_TYPES.CLOCK_OUT,
            metadata: {
              shiftId,
              teamId: shiftData.teamId
            }
          }
        }));
      } catch (rewardError) {
        console.error('Error awarding rewards:', rewardError);
        // Don't fail the clock out if rewards fail
      }

      // Award rewards for shift complete
      try {
        const { ACTIVITY_TYPES } = await import('../utils/gamificationManager');
        window.dispatchEvent(new CustomEvent('awardActivity', {
          detail: {
            activityType: ACTIVITY_TYPES.SHIFT_COMPLETE,
            metadata: {
              shiftId,
              teamId: shiftData.teamId
            }
          }
        }));
      } catch (rewardError) {
        console.error('Error awarding rewards:', rewardError);
        // Don't fail the clock out if rewards fail
      }

      return { success: true };
    } catch (err) {
      console.error('Error clocking out:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Request Shift Swap
  const requestShiftSwap = async (shiftId, swapData) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const shiftRef = doc(db, 'shifts', shiftId);
      const shiftSnap = await getDoc(shiftRef);

      if (!shiftSnap.exists()) {
        throw new Error('ไม่พบเวร');
      }

      const shiftData = shiftSnap.data();

      if (shiftData.assignedTo !== currentUser.uid) {
        throw new Error('คุณไม่ได้ถูกมอบหมายให้เวรนี้');
      }

      // Create swap request
      const swapRequest = {
        teamId: shiftData.teamId,
        planId: shiftData.planId || null, // Reference to shiftPlans (if from plan)
        originalShiftId: shiftId,
        originalShift: {
          shiftType: shiftData.shiftType,
          startTime: shiftData.startTime,
          endTime: shiftData.endTime
        },
        requestedBy: currentUser.uid,
        requestedByName: currentUser.displayName || currentUser.email,
        requestedTo: swapData.requestedTo,
        requestedToName: swapData.requestedToName,
        requestedShiftId: swapData.requestedShiftId || null,
        message: swapData.message || '',
        status: 'PENDING',
        requiresLeaderApproval: !!shiftData.planId, // Require leader approval if from plan
        approvedBy: null,
        approvedAt: null,
        rejectedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const swapRef = await addDoc(collection(db, 'shiftSwaps'), swapRequest);

      // Send notification to requested user
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          swapData.requestedTo,
          notificationModel.types.SHIFT_SWAP_REQUEST,
          '🔄 มีคำขอแลกเวร',
          `${currentUser.displayName || currentUser.email} ขอแลกเวรกับคุณ`,
          {
            swapId: swapRef.id,
            shiftId: shiftId,
            teamId: shiftData.teamId
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      return { success: true, swapId: swapRef.id };
    } catch (err) {
      console.error('Error requesting shift swap:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve/Reject Shift Swap
  const handleShiftSwap = async (swapId, action) => {
    try {
      if (!currentUser) {
        throw new Error('กรุณาเข้าสู่ระบบ');
      }

      const swapRef = doc(db, 'shiftSwaps', swapId);
      const swapSnap = await getDoc(swapRef);

      if (!swapSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const swapData = swapSnap.data();

      if (swapData.requestedTo !== currentUser.uid) {
        throw new Error('คุณไม่ใช่ผู้รับคำขอ');
      }

      if (action === 'APPROVE') {
        // If swap requires leader approval (from plan), just mark as approved by member
        // and notify leader. Otherwise, complete the swap immediately.
        if (swapData.requiresLeaderApproval && swapData.planId) {
          // Update swap request status to MEMBER_APPROVED (waiting for leader)
          await updateDoc(swapRef, {
            status: 'MEMBER_APPROVED',
            memberApprovedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          // Send notification to requester
          try {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              swapData.requestedBy,
              notificationModel.types.SHIFT_SWAP_APPROVED,
              '✅ คำขอแลกเวรถูกตอบรับ',
              `${currentUser.displayName || currentUser.email} ตอบรับคำขอแลกเวรของคุณแล้ว รอหัวหน้าทีมอนุมัติ`,
              {
                swapId: swapId,
                shiftId: swapData.originalShiftId,
                teamId: swapData.teamId,
                planId: swapData.planId
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } catch (notifError) {
            console.error('Error sending notification:', notifError);
          }

          // Send notification to team leader
          try {
            // Get team leader
            const teamRef = doc(db, 'teams', swapData.teamId);
            const teamSnap = await getDoc(teamRef);
            if (teamSnap.exists()) {
              const teamData = teamSnap.data();
              const leaderId = teamData.leaderId || teamData.createdBy;
              
              if (leaderId) {
                const notificationsRef = collection(db, notificationModel.collection);
                const notification = notificationModel.createNotification(
                  leaderId,
                  notificationModel.types.SHIFT_SWAP_LEADER_APPROVAL,
                  '🔄 มีคำขอแลกเวรรออนุมัติ',
                  `${swapData.requestedByName} และ ${swapData.requestedToName} ขอแลกเวร รอการอนุมัติ`,
                  {
                    swapId: swapId,
                    shiftId: swapData.originalShiftId,
                    teamId: swapData.teamId,
                    planId: swapData.planId
                  }
                );
                await addDoc(notificationsRef, {
                  ...notification,
                  read: false,
                  readAt: null,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                });
              }
            }
          } catch (notifError) {
            console.error('Error sending notification to leader:', notifError);
          }
        } else {
          // Complete swap immediately (not from plan)
          const originalShiftRef = doc(db, 'shifts', swapData.originalShiftId);
          await updateDoc(originalShiftRef, {
            assignedTo: currentUser.uid,
            assignedToName: currentUser.displayName || currentUser.email,
            updatedAt: serverTimestamp()
          });

          // If requestedShiftId exists, swap both shifts
          if (swapData.requestedShiftId) {
            const requestedShiftRef = doc(db, 'shifts', swapData.requestedShiftId);
            await updateDoc(requestedShiftRef, {
              assignedTo: swapData.requestedBy,
              assignedToName: swapData.requestedByName,
              updatedAt: serverTimestamp()
            });
          }

          // Update swap request
          await updateDoc(swapRef, {
            status: 'APPROVED',
            approvedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          // Send notification to requester
          try {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              swapData.requestedBy,
              notificationModel.types.SHIFT_SWAP_APPROVED,
              '✅ คำขอแลกเวรถูกอนุมัติ',
              `${currentUser.displayName || currentUser.email} อนุมัติคำขอแลกเวรของคุณแล้ว`,
              {
                swapId: swapId,
                shiftId: swapData.originalShiftId,
                teamId: swapData.teamId
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } catch (notifError) {
            console.error('Error sending notification:', notifError);
          }
        }
      } else {
        // Reject swap request
        await updateDoc(swapRef, {
          status: 'REJECTED',
          rejectedAt: serverTimestamp()
        });

        // Send notification to requester
        try {
          const notificationsRef = collection(db, notificationModel.collection);
          const notification = notificationModel.createNotification(
            swapData.requestedBy,
            notificationModel.types.SHIFT_SWAP_REJECTED,
            '❌ คำขอแลกเวรถูกปฏิเสธ',
            `${currentUser.displayName || currentUser.email} ปฏิเสธคำขอแลกเวรของคุณ`,
            {
              swapId: swapId,
              shiftId: swapData.originalShiftId,
              teamId: swapData.teamId
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Error handling shift swap:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get Upcoming Shifts
  const getUpcomingShifts = () => {
    const now = new Date();
    return shifts.filter(shift => {
      const startTime = shift.startTime?.toDate();
      return startTime && startTime > now && shift.status === SHIFT_STATUS.SCHEDULED;
    }).sort((a, b) => {
      const aTime = a.startTime?.toDate();
      const bTime = b.startTime?.toDate();
      return aTime - bTime;
    });
  };

  // Get Today's Shifts
  const getTodayShifts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return shifts.filter(shift => {
      const startTime = shift.startTime?.toDate();
      return startTime && startTime >= today && startTime < tomorrow;
    });
  };

  const value = {
    shifts,
    loading,
    error,
    createShift,
    clockIn,
    clockOut,
    requestShiftSwap,
    handleShiftSwap,
    getUpcomingShifts,
    getTodayShifts
  };

  return (
    <ShiftContext.Provider value={value}>
      {children}
    </ShiftContext.Provider>
  );
};


